/**
 * LOGO DE CLIENTE EN LISTADOS — LECTURA PEREZOSA Y CACHEADA (UX-POLISH-01, punto 1)
 *
 * Ver la cabecera de `almacenamiento/logos.ts` (backend) para las dos
 * vías de lectura. Este módulo es el lado cliente de la vía "listado":
 * una fila sólo trae `tieneLogo: boolean` (barato); si es `true`, se
 * pide aparte `GET /api/clientes/:id/logo-imagen` con el mismo Bearer
 * JWT de siempre — un `<img src>` directo no puede mandar esa cabecera,
 * de ahí `fetch` + blob URL en vez de apuntar `src` a la ruta — y el
 * resultado se cachea en memoria por `idCliente`: una fila repetida
 * (mismo cliente, varios centros en la tabla de Centros de una ficha)
 * no vuelve a pedirlo.
 *
 * El caché vive mientras dure la pestaña. No hay invalidación al
 * cambiar el logo porque la edición (`GestorLogoCliente`) está en la
 * ficha de detalle, que usa la OTRA vía (`data:` URI de la misma
 * respuesta JSON), nunca ésta — si algún día un listado permite editar
 * el logo in situ, esto necesitará invalidar la entrada correspondiente.
 */
import { useEffect, useState } from 'react';

import { tokenGuardado } from '../api.ts';

const cache = new Map<string, Promise<string | null>>();

function cargar(idCliente: string): Promise<string | null> {
  const existente = cache.get(idCliente);
  if (existente !== undefined) return existente;

  const promesa = (async (): Promise<string | null> => {
    const token = tokenGuardado();
    if (token === null) return null;
    try {
      const respuesta = await fetch(`/api/clientes/${idCliente}/logo-imagen`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!respuesta.ok) return null;
      const blob = await respuesta.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  })();

  cache.set(idCliente, promesa);
  return promesa;
}

/**
 * `null` mientras no hay logo (o aún no ha llegado la respuesta) — se
 * pasa tal cual a `Avatar`, que ya sabe caer a iniciales con `null`.
 */
export function useLogoRemoto(idCliente: string, tieneLogo: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!tieneLogo) {
      setUrl(null);
      return;
    }
    let vivo = true;
    void cargar(idCliente).then((resultado) => {
      if (vivo) setUrl(resultado);
    });
    return () => {
      vivo = false;
    };
  }, [idCliente, tieneLogo]);

  return url;
}
